/**
 * Utilitário centralizado de padronização do campo confianca.
 * Banco de Dados: 0.00 até 1.00 (NUMERIC(3,2))
 * Interface / Usuário: 0% até 100%
 */

/**
 * Normaliza qualquer valor de confiança para a escala do Banco de Dados [0.00, 1.00].
 * Trata valores passados como percentual (ex: 90 -> 0.90, 100 -> 1.00) ou
 * valores já normalizados (ex: 0.85 -> 0.85).
 * Clampa o resultado estritamente no intervalo [0.00, 1.00].
 *
 * Exemplos:
 *  90    -> 0.90
 *  100   -> 1.00
 *  0.85  -> 0.85
 *  250   -> 1.00
 *  -10   -> 0.00
 *  null  -> 0.00
 */
export function normalizeConfidence(value: number | null | undefined): number {
  if (value == null || isNaN(value)) return 0;

  let val = Number(value);

  // Se maior que 1, assume que foi enviado como porcentagem (ex: 90 -> 0.9)
  if (val > 1) {
    val = val / 100;
  }

  // Clampa no intervalo [0, 1]
  const clamped = Math.max(0, Math.min(1, val));

  // Arredonda para 2 casas decimais para compatibilidade perfeita com NUMERIC(3,2)
  return Math.round(clamped * 100) / 100;
}

/**
 * Converte valor de confiança para porcentagem inteira [0, 100].
 *
 * Exemplos:
 *  0.90 -> 90
 *  1.00 -> 100
 *  0.85 -> 85
 *  90   -> 90
 */
export function confidenceToPercent(value: number | null | undefined): number {
  const normalized = normalizeConfidence(value);
  return Math.round(normalized * 100);
}

/**
 * Converte porcentagem [0, 100] para valor de confiança normalizado [0.00, 1.00].
 *
 * Exemplos:
 *  90   -> 0.90
 *  100  -> 1.00
 *  0.85 -> 0.85
 */
export function percentToConfidence(percent: number | null | undefined): number {
  if (percent == null || isNaN(percent)) return 0;
  return normalizeConfidence(percent);
}

/**
 * Formata valor de confiança para exibição textual em UI com símbolo de %.
 * Exemplo: 0.9 -> "90%"
 */
export function formatConfidencePercent(value: number | null | undefined): string {
  if (value == null || isNaN(Number(value))) return '0%';
  return `${confidenceToPercent(value)}%`;
}
